import { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { X, Check, AlertTriangle, Camera } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

/**
 * QRScanner — full-screen camera scanner. Calls POST /api/checkin with {token}
 * onResult(result) is called after each scan; scanner stays open for the next scan.
 */
export default function QRScanner({ open, onClose, onResult }) {
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [cameraError, setCameraError] = useState("");
  const scannerRef = useRef(null);
  const containerId = "qr-reader-el";
  const lastTokenRef = useRef({ token: "", at: 0 });

  useEffect(() => {
    if (!open) return;
    let stopped = false;
    setCameraError("");
    const start = async () => {
      try {
        const q = new Html5Qrcode(containerId, { verbose: false });
        scannerRef.current = q;
        await q.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 250, height: 250 } },
          async (decoded) => {
            const now = Date.now();
            if (decoded === lastTokenRef.current.token && now - lastTokenRef.current.at < 3000) return;
            lastTokenRef.current = { token: decoded, at: now };
            setBusy(true);
            try {
              const { data } = await api.post("/checkin", { token: decoded });
              setResult({ ok: true, ...data });
              toast.success(data.already_checked ? "Déjà pointé" : `${data.participant.name} — présent`);
              onResult && onResult(data);
            } catch (e) {
              setResult({ ok: false, message: formatApiError(e.response?.data?.detail) || "Erreur" });
              toast.error("Badge invalide");
            } finally {
              setBusy(false);
            }
          },
          () => {} // silent decode errors
        );
      } catch (e) {
        setCameraError(String(e?.message || e) + " — autorisez la caméra dans votre navigateur.");
      }
    };
    start();
    return () => {
      stopped = true;
      const q = scannerRef.current;
      if (q) {
        q.stop().then(() => q.clear()).catch(() => {});
        scannerRef.current = null;
      }
    };
  }, [open]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" data-testid="qr-scanner-modal">
      <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2 font-semibold text-[var(--pine)]"><Camera size={18}/> Scanner un badge</div>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded-full" data-testid="qr-scanner-close"><X size={18}/></button>
        </div>
        <div id={containerId} className="w-full aspect-square bg-black" />
        {cameraError && (
          <div className="p-4 text-sm text-red-600 flex items-start gap-2"><AlertTriangle size={16}/><span>{cameraError}</span></div>
        )}
        <div className="p-4 min-h-[80px]">
          {busy && <div className="text-sm text-[var(--muted-text)]">Vérification…</div>}
          {result && result.ok && (
            <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-sm" data-testid="qr-scan-result-ok">
              <div className="flex items-center gap-2 font-semibold text-emerald-800"><Check size={16}/>{result.already_checked ? "Déjà pointé" : "Pointage enregistré"}</div>
              <div className="mt-1">{result.participant?.name} · {result.session?.title}</div>
              <div className="text-xs text-[var(--muted-text)]">{result.paid ? `Payé (${result.payment_method || "-"})` : "⚠ Paiement non enregistré"}</div>
            </div>
          )}
          {result && !result.ok && (
            <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700" data-testid="qr-scan-result-err">{result.message}</div>
          )}
          <div className="text-xs text-[var(--muted-text)] mt-2">Prochain badge : présentez-le devant l'objectif.</div>
        </div>
      </div>
    </div>
  );
}
